import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { User } from '../../../models/User/user';
import { AppContextService } from '../../../services/app-context.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { FaqQuestion } from '../../../models/help/faqs/faq-question';
import { FaqsCategory } from '../../../models/help/faqs/faqs.category';
import { AccordionItem } from '../../../shared-components/custom-accordion/custom-accordion.component';
import { FAQQuestion, FAQCategory } from '../../../shared-components/faq-manager/faq-manager.types';
import { getQuestionText, getAnswerText, getCategoryText } from '../../../shared-components/faq-manager/faq-manager.utils';
import { FaqManagementApiService } from '../../../server-api/faq-management/faq-management.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';

interface FAQCategoryGroup {
	category: FaqsCategory;
	questions: FaqQuestion[];
	accordionItems: AccordionItem[];
	isExpanded?: boolean;
}

interface FAQGroup {
	product: string;
	profile: string;
	categories: FAQCategoryGroup[];
	isExpanded?: boolean;
}

interface FaqQuestionWithFlags extends FaqQuestion {
	factoring?: boolean;
	confirming?: boolean;
	buyer?: boolean;
	supplier?: boolean;
}

@Component({
	selector: 'app-faq-showcase',
	templateUrl: './faq-showcase.component.html',
	styleUrls: ['./faq-showcase.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class FAQShowcaseComponent implements OnInit, OnDestroy {
	public loggedUser: User | null = null;
	public isLoading: boolean = false;
	public faqGroups: FAQGroup[] = [];
	public filteredFaqGroups: FAQGroup[] = [];

	// Filter properties
	public filterProduct: string[] = [];
	public filterProfile: string[] = [];
	public filterCategory: string[] = [];
	public filterSearch: string = '';
	public availableProducts: string[] = [];
	public availableProfiles: string[] = [];
	public availableCategories: Array<{ value: string; label: string }> = [];

	// Stable arrays for select options — only recomputed in updateAvailableFilters()
	public productOptions: Array<{ value: string; label: string }> = [];
	public profileOptions: Array<{ value: string; label: string }> = [];

	private allFaqGroups: FAQGroup[] = [];
	private subscriptions: Subscription[] = [];
	private readonly chevronRightIcon = '<i class="fa-solid fa-angle-right"></i>'; // Collapsed state
	private readonly chevronDownIcon = '<i class="fa-solid fa-angle-down"></i>'; // Expanded state

	constructor(
		public appContext: AppContextService,
		private cdr: ChangeDetectorRef,
		private elRef: ElementRef,
		public translateService: TranslateService,
		private faqManagementApiService: FaqManagementApiService,
		private toasterService: ToasterService
	) {}

	/**
	 * Update chevron icon by clearing DOM and setting innerHTML
	 * Follows the same pattern as custom-select component
	 * Normalized: collapsed = right, expanded = down (same as accordion)
	 */
	private updateChevronIcon(element: HTMLElement, isExpanded: boolean): void {
		if (!element) return;

		// Clear DOM before updating (same pattern as custom-select)
		while (element.firstChild) {
			element.removeChild(element.firstChild);
		}

		// Set innerHTML with new icon (normalized: collapsed = right, expanded = down)
		element.innerHTML = isExpanded ? this.chevronDownIcon : this.chevronRightIcon;
	}

	ngOnInit(): void {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			this.loadFAQsFromBackend();
			this.cdr.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((sub) => {
			if (sub && !sub.closed) {
				sub.unsubscribe();
			}
		});
		this.subscriptions = [];
	}

	private loadFAQsFromBackend(): void {
		this.isLoading = true;
		this.cdr.markForCheck();

		const sub = forkJoin([
			this.faqManagementApiService.getFaqViewerCategories(),
			this.faqManagementApiService.getFaqViewerQuestions(),
		]).subscribe({
			next: ([categoriesResult, questionsResult]) => {
				if (!categoriesResult.IsValid || !questionsResult.IsValid) {
					const errors = [...(categoriesResult.Errors || []), ...(questionsResult.Errors || [])];
					const message = errors.map((e) => e.Code).join('\n');
					this.toasterService.showError(message || '');
					this.isLoading = false;
					this.cdr.markForCheck();
					return;
				}

				const faqCategories: FAQCategory[] = categoriesResult.Data || [];
				const faqQuestions: FAQQuestion[] = questionsResult.Data || [];

				if (faqCategories.length === 0 || faqQuestions.length === 0) {
					this.isLoading = false;
					this.cdr.markForCheck();
					return;
				}

				const languageCode = this.loggedUser?.LanguageCode ?? this.appContext.getDefaultLanguageCode();

				const convertedQuestions: FaqQuestionWithFlags[] = faqQuestions.map((q) => {
					let questionText = '';
					let answerText = '';

					switch (languageCode.toLowerCase()) {
						case 'pt':
						case 'pt-br':
							questionText = getQuestionText(q, 'pt');
							answerText = getAnswerText(q, 'pt');
							break;
						case 'en':
						case 'en-us':
							questionText = getQuestionText(q, 'en');
							answerText = getAnswerText(q, 'en');
							break;
						case 'es':
							questionText = getQuestionText(q, 'es');
							answerText = getAnswerText(q, 'es');
							break;
						default:
							questionText = getQuestionText(q, 'en') || getQuestionText(q, 'pt') || getQuestionText(q, 'es');
							answerText = getAnswerText(q, 'en') || getAnswerText(q, 'pt') || getAnswerText(q, 'es');
					}

					return {
						Id: q.questionId,
						CategoryCode: q.categoryCode,
						PresentationOrder: q.questionPresentationOrder,
						FaqQuestionText: {
							QuestionId: q.questionId,
							LanguageCode: languageCode,
							Question: questionText,
							Answer: answerText,
						},
						factoring: q.factoring,
						confirming: q.confirming,
						buyer: q.buyer,
						supplier: q.supplier,
					} as FaqQuestionWithFlags;
				});

				const convertedCategories: FaqsCategory[] = faqCategories.map((c) => {
					let categoryText = '';
					switch (languageCode.toLowerCase()) {
						case 'pt':
						case 'pt-br':
							categoryText = getCategoryText(c, 'pt') || c.categoryCode;
							break;
						case 'en':
						case 'en-us':
							categoryText = getCategoryText(c, 'en') || c.categoryCode;
							break;
						case 'es':
							categoryText = getCategoryText(c, 'es') || c.categoryCode;
							break;
						default:
							categoryText = getCategoryText(c, 'en') || getCategoryText(c, 'pt') || getCategoryText(c, 'es') || c.categoryCode;
					}

					return {
						CategoryCode: c.categoryCode,
						CategoryCodeText: categoryText,
						PresentationOrder: c.categoryPresentationOrder,
					} as FaqsCategory;
				});

				this.processFAQs(convertedCategories, convertedQuestions);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				this.isLoading = false;
				this.cdr.markForCheck();
			},
		});

		this.subscriptions.push(sub);
	}

	private escapeHtml(text: string): string {
		return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	private highlightText(text: string, term: string): string {
		if (!term) return this.escapeHtml(text);
		const escaped = this.escapeHtml(text);
		const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escapedTerm})`, 'gi');
		return escaped.replace(regex, '<mark class="faq-highlight">$1</mark>');
	}

	private highlightHtml(html: string, term: string): string {
		if (!term || !html) return html;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
		const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
		const textNodes: Text[] = [];
		let node: Node | null;
		while ((node = walker.nextNode())) {
			textNodes.push(node as Text);
		}
		textNodes.forEach((textNode) => {
			const text = textNode.textContent ?? '';
			if (!regex.test(text)) return;
			regex.lastIndex = 0;
			const parent = textNode.parentNode;
			if (!parent) return;
			const fragment = doc.createDocumentFragment();
			const parts = text.split(regex);
			parts.forEach((part, i) => {
				if (i % 2 === 1) {
					const mark = doc.createElement('mark');
					mark.className = 'faq-highlight';
					mark.textContent = part;
					fragment.appendChild(mark);
				} else if (part) {
					fragment.appendChild(doc.createTextNode(part));
				}
			});
			parent.replaceChild(fragment, textNode);
		});
		return doc.body.innerHTML;
	}

	private processFAQs(categories: FaqsCategory[], questions: FaqQuestionWithFlags[]): void {
		// Create a map of categories by code for quick lookup
		const categoryMap = new Map<string, FaqsCategory>();
		categories.forEach((cat) => {
			categoryMap.set(cat.CategoryCode, cat);
		});

		// Group questions by PRODUCT + PROFILE
		const groupedMap = new Map<string, Map<string, FaqQuestion[]>>();

		questions.forEach((question) => {
			const combinations: Array<{ product: string; profile: string }> = [];

			const isFactoring = question.factoring === true;
			const isConfirming = question.confirming === true;
			const isBuyer = question.buyer === true;
			const isSupplier = question.supplier === true;

			if (isFactoring && isSupplier) {
				combinations.push({ product: 'Factoring', profile: 'Supplier' });
			}
			if (isConfirming) {
				if (isBuyer) combinations.push({ product: 'Confirming', profile: 'Buyer' });
				if (isSupplier) combinations.push({ product: 'Confirming', profile: 'Supplier' });
			}

			// Add question to each combination group, grouped by category
			combinations.forEach(({ product, profile }) => {
				const groupKey = `${product}-${profile}`;
				if (!groupedMap.has(groupKey)) {
					groupedMap.set(groupKey, new Map<string, FaqQuestion[]>());
				}
				const categoryQuestionsMap = groupedMap.get(groupKey)!;
				const categoryCode = question.CategoryCode || 'Unknown';
				if (!categoryQuestionsMap.has(categoryCode)) {
					categoryQuestionsMap.set(categoryCode, []);
				}
				const categoryQuestions = categoryQuestionsMap.get(categoryCode)!;
				if (question) {
					categoryQuestions.push(question);
				}
			});
		});

		// Convert map to array and sort by product/profile
		const groups = Array.from(groupedMap.entries())
			.map(([key, categoryQuestionsMap]) => {
				const [product, profile] = key.split('-');
				return { product, profile, categoryQuestionsMap };
			})
			.sort((a, b) => {
				if (a.product !== b.product) {
					return a.product.localeCompare(b.product);
				}
				return a.profile.localeCompare(b.profile);
			});

		// Build category groups for each product/profile group
		const newGroups: FAQGroup[] = [];

		for (const group of groups) {
			const categoryGroups: FAQCategoryGroup[] = [];

			// Convert category map to array and sort by category presentation order
			const categoryEntries = Array.from(group.categoryQuestionsMap.entries())
				.map(([categoryCode, questions]) => {
					const category = categoryMap.get(categoryCode);
					if (!category) {
						// If category not found, create a placeholder
						return {
							category: {
								CategoryCode: categoryCode,
								CategoryCodeText: categoryCode,
								PresentationOrder: 9999, // Put unknown categories at the end
							} as FaqsCategory,
							questions,
						};
					}
					return { category, questions };
				})
				.sort((a, b) => (a.category.PresentationOrder || 0) - (b.category.PresentationOrder || 0));

			// Build accordion items for each category
			for (const { category, questions: categoryQuestions } of categoryEntries) {
				// Sort questions by presentation order
				const sortedQuestions = [...categoryQuestions].sort((a, b) => (a.PresentationOrder || 0) - (b.PresentationOrder || 0));

				const accordionItems = this.buildCategoryAccordionItems(group.product, group.profile, category, sortedQuestions);

				// Only add categories with valid accordion items
				if (accordionItems && accordionItems.length > 0) {
					const validItems = accordionItems.filter((item) => item != null && item.title != null && item.content != null && item.id != null);
					if (validItems.length > 0) {
						categoryGroups.push({
							category,
							questions: sortedQuestions,
							accordionItems: validItems,
							isExpanded: true,
						});
					}
				}
			}

			// Only add product/profile groups that have categories
			if (categoryGroups.length > 0) {
				newGroups.push({
					product: group.product,
					profile: group.profile,
					categories: categoryGroups,
					isExpanded: true, // Groups expanded by default
				});
			}
		}

		this.allFaqGroups = newGroups;
		this.faqGroups = newGroups; // Keep faqGroups for template compatibility
		this.updateAvailableFilters();
		this.applyFilters();
		this.isLoading = false;

		// Initialize chevron icons using DOM manipulation (same pattern as custom-select)
		setTimeout(() => {
			this.updateChevronIcons();
		}, 0);

		// Trigger change detection manually since we're using OnPush
		this.cdr.markForCheck();
	}

	toggleGroup(group: FAQGroup, event?: Event): void {
		group.isExpanded = !group.isExpanded;

		// Update chevron icon using DOM manipulation (same pattern as custom-select)
		if (event) {
			const target = event.currentTarget as HTMLElement;
			const chevronElement = target.querySelector('.faq-showcase__group-chevron') as HTMLElement;
			if (chevronElement) {
				this.updateChevronIcon(chevronElement, !!group.isExpanded);
			}
		}

		this.cdr.markForCheck();
	}

	toggleCategory(categoryGroup: FAQCategoryGroup, event?: Event): void {
		categoryGroup.isExpanded = !categoryGroup.isExpanded;

		// Update chevron icon using DOM manipulation (same pattern as custom-select)
		if (event) {
			const target = event.currentTarget as HTMLElement;
			const chevronElement = target.querySelector('.faq-showcase__category-chevron') as HTMLElement;
			if (chevronElement) {
				this.updateChevronIcon(chevronElement, !!categoryGroup.isExpanded);
			}
		}

		this.cdr.markForCheck();
	}

	private updateChevronIcons(): void {
		// Update all group chevrons
		const groupChevrons = this.elRef.nativeElement.querySelectorAll('.faq-showcase__group-chevron');
		groupChevrons.forEach((el: HTMLElement, index: number) => {
			if (this.filteredFaqGroups[index]) {
				this.updateChevronIcon(el, !!this.filteredFaqGroups[index].isExpanded);
			}
		});

		// Update all category chevrons
		const categoryChevrons = this.elRef.nativeElement.querySelectorAll('.faq-showcase__category-chevron');
		let categoryIndex = 0;
		this.filteredFaqGroups.forEach((group) => {
			group.categories.forEach((categoryGroup) => {
				if (categoryChevrons[categoryIndex]) {
					this.updateChevronIcon(categoryChevrons[categoryIndex], !!categoryGroup.isExpanded);
					categoryIndex++;
				}
			});
		});
	}

	private buildCategoryAccordionItems(product: string, profile: string, category: FaqsCategory, questions: FaqQuestion[]): AccordionItem[] {
		const items: AccordionItem[] = [];

		if (!questions || questions.length === 0) {
			return items;
		}

		questions.forEach((question) => {
			if (!question || !question.Id) {
				return;
			}

			const questionText = this.getQuestionText(question);
			const answerText = this.getAnswerText(question);

			if (questionText && answerText && questionText.trim() && answerText.trim()) {
				items.push({
					id: `${product}-${profile}-${category.CategoryCode}-${question.Id}`,
					title: questionText.trim(),
					content: answerText.trim(),
					isExpanded: false,
				});
			}
		});

		return items;
	}

	private getQuestionText(question: FaqQuestion): string {
		if (!question.FaqQuestionText) {
			return '';
		}

		// FaqQuestionText is already filtered by language from the API
		return question.FaqQuestionText.Question || '';
	}

	private getAnswerText(question: FaqQuestion): string {
		if (!question.FaqQuestionText) {
			return '';
		}

		// FaqQuestionText is already filtered by language from the API
		return question.FaqQuestionText.Answer || '';
	}

	private updateAvailableFilters(): void {
		// Extract unique products and profiles
		const products = new Set<string>();
		const profiles = new Set<string>();
		// Use CategoryCodeText as both key and value to deduplicate by translated text
		const categories = new Set<string>();

		this.allFaqGroups.forEach((group) => {
			products.add(group.product);
			profiles.add(group.profile);
			group.categories.forEach((catGroup) => {
				// Use CategoryCodeText to deduplicate (same translation text, different codes)
				if (catGroup.category.CategoryCodeText) {
					categories.add(catGroup.category.CategoryCodeText);
				}
			});
		});

		this.availableProducts = Array.from(products).sort();
		this.availableProfiles = Array.from(profiles).sort();

		this.productOptions = this.availableProducts.map((p) => {
			const translated = this.translateService.translate(p, 'client');
			const label = translated && translated.toLowerCase() !== p.toLowerCase() ? translated : p;
			return { value: p, label };
		});
		this.profileOptions = this.availableProfiles.map((p) => {
			const translated = this.translateService.translate(p, 'list-role');
			const label = translated && translated.toLowerCase() !== p.toLowerCase() ? translated : p;
			return { value: p, label };
		});
		this.availableCategories = Array.from(categories)
			.map((categoryText) => ({ value: categoryText, label: categoryText }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	applyFilters(): void {
		let filtered = [...this.allFaqGroups];

		// Filter by product (multiple selection)
		if (this.filterProduct && this.filterProduct.length > 0) {
			filtered = filtered.filter((group) => this.filterProduct.includes(group.product));
		}

		// Filter by profile (multiple selection)
		if (this.filterProfile && this.filterProfile.length > 0) {
			filtered = filtered.filter((group) => this.filterProfile.includes(group.profile));
		}

		// Filter by category, question text, and answer content
		const hasTextFilters = (this.filterCategory && this.filterCategory.length > 0) || this.filterSearch;
		if (hasTextFilters) {
			filtered = filtered
				.map((group) => {
					const filteredCategories = group.categories
						.map((catGroup) => {
							// Filter by category using CategoryCodeText (translated text) - multiple selection
							if (
								this.filterCategory &&
								this.filterCategory.length > 0 &&
								!this.filterCategory.includes(catGroup.category.CategoryCodeText)
							) {
								return null;
							}

							// Filter questions by searching across both question text and answer content
							const filteredItems = catGroup.accordionItems.filter((item) => {
								const term = this.filterSearch.toLowerCase();
								return !this.filterSearch || item.title.toLowerCase().includes(term) || item.content?.toLowerCase().includes(term);
							});

							// Only include category if it has matching questions
							if (filteredItems.length === 0) {
								return null;
							}

							// Apply highlight to matching text when a search term is active
							const displayItems = this.filterSearch
								? filteredItems.map((item) => ({
										...item,
										safeTitle: this.highlightText(item.title, this.filterSearch),
										content: this.highlightHtml(item.content ?? '', this.filterSearch),
									}))
								: filteredItems;

							return {
								...catGroup,
								accordionItems: displayItems,
							};
						})
						.filter((cat): cat is FAQCategoryGroup => cat !== null);

					// Only include group if it has matching categories
					if (filteredCategories.length === 0) {
						return null;
					}

					return {
						...group,
						categories: filteredCategories,
					};
				})
				.filter((group): group is FAQGroup => group !== null);
		}

		this.filteredFaqGroups = filtered;

		// Update chevron icons after filtering
		setTimeout(() => {
			this.updateChevronIcons();
		}, 0);

		this.cdr.markForCheck();
	}

	onFilterChange(): void {
		this.applyFilters();
	}

	clearFilters(): void {
		this.filterProduct = [];
		this.filterProfile = [];
		this.filterCategory = [];
		this.filterSearch = '';
		this.applyFilters();
	}

	hasActiveFilters(): boolean {
		return !!(
			(this.filterProduct && this.filterProduct.length > 0) ||
			(this.filterProfile && this.filterProfile.length > 0) ||
			(this.filterCategory && this.filterCategory.length > 0) ||
			this.filterSearch
		);
	}

	setFaqGroupTranslation(group: FAQGroup): string {
		return `${group.product} - ${this.translateService.translate(group.profile, 'list-role')}`;
	}
}
